import { CHAT_CLOSE, CHAT_OPEN } from 'constants/mixpanel';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useUser } from 'modules/User';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import initialize from './initialize';
import ZendeskContext from './zendeskContext';

// Declare global interfaces for window object to support zendesk global variable
declare global {
  interface Window {
    zE?: Zendesk_.ZendeskStatic;
  }
}

const zendeskAppId = process.env.REACT_APP_ZENDESK_APP_ID as string;

interface ZendeskContextProviderProps {
  children: ReactNode;
}
type ZendeskContextType = Zendesk_.ZendeskCommandSignature;

// ZendeskProvider component that wraps its children with the Zendesk context
export const ZendeskProvider = ({ children }: ZendeskContextProviderProps) => {
  const { venngageE2 } = useVenngageE1Gate();
  const shouldHideChat = venngageE2?.isUserStudent;

  // Show the Zendesk messenger
  const show = useCallback(() => {
    if (!!window.zE) {
      window.zE('messenger', 'open');
    }
  }, []);

  // Track when the chat icon is clicked
  const onShow = useCallback(() => {
    Mixpanel.track(CHAT_OPEN.eventName);
  }, []);

  // Track when the chat is hidden
  const onHide = useCallback(() => {
    Mixpanel.track(CHAT_CLOSE.eventName);
  }, []);

  // State to store whether the Zendesk chat is loaded or not
  const [isLoaded, setIsLoaded] = useState(false);

  // Provider values for Zendesk context
  const providerValues = {
    show,
    onShow,
    onHide,
  };

  const { data: userInfo } = useUser();

  // Effect to initialize Zendesk chat for first time
  useEffect(() => {
    if (!shouldHideChat && userInfo && !!userInfo.use_intercom && !window.zE && !isLoaded) {
      setIsLoaded(true);
      initialize(zendeskAppId, 0, onShow, onHide);
    }
  }, [userInfo, isLoaded, onShow, onHide, shouldHideChat]);

  return <ZendeskContext.Provider value={providerValues}>{children}</ZendeskContext.Provider>;
};

// Custom hook to use the Zendesk context
export const useZendesk = () => {
  const zendeskContext = useContext(ZendeskContext);
  return zendeskContext as ZendeskContextType;
};
